import React, { FC, FocusEventHandler, KeyboardEventHandler } from 'react';
import {
  ActionMeta,
  CSSObjectWithLabel,
  GroupBase,
  InputActionMeta,
  MultiValue,
  OptionsOrGroups,
  PlaceholderProps,
  StylesConfig,
} from 'react-select';
import { FR_PR_BLUE, FR_PR_WHITE } from '@shared/styles/vars';
import { MultiSelectDropdownInputBase } from '../MultiSelectDropdownInputBase';
import { Option } from '../interfaces/option.interface';
import { controlStyles, optionStyles } from '../styles/selectStyles';

interface MultiSelectDropdownInputProps {
  className?: string;
  inputValue?: string;
  isInvalid?: boolean;
  isMenuOpen?: boolean;
  onChange?: (newValue: MultiValue<Option>, actionMeta: ActionMeta<Option>) => void;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onInputChange?: (newValue: string, actionMeta: InputActionMeta) => void;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  options?: OptionsOrGroups<Option, GroupBase<Option>>;
  placeholder?: string;
  styles?: StylesConfig<Option, true, GroupBase<Option>>;
  value?: MultiValue<Option>;
  wrapperClassName?: string;
}

const valueContainerStyles = (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
  ...base,
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  '-ms-overflow-style': 'none',
  'display': 'block',
  'overflowX': 'auto',
  'scrollbarWidth': 'none',
  'whiteSpace': 'nowrap',
});

const placeholderStyles = (
  base: CSSObjectWithLabel,
  props: PlaceholderProps<Option, true, GroupBase<Option>>,
): CSSObjectWithLabel => ({
  ...base,
  display: props.isFocused ? 'none' : 'inline-block',
});

const multiValueStyles = (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
  ...base,
  backgroundColor: FR_PR_BLUE,
  border: 'none',
  borderRadius: '0.6rem',
  boxShadow: 'none',
  display: 'inline-flex',
});

const multiValueLabelStyles = (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
  ...base,
  color: FR_PR_WHITE,
});

const multiValueRemoveStyles = (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
  ...base,
  '&:hover': {
    backgroundColor: 'transparent',
    color: FR_PR_WHITE,
  },
  'backgroundColor': 'transparent',
  'color': FR_PR_WHITE,
});

export const MultiSelectDropdownInput: FC<MultiSelectDropdownInputProps> = ({
  className,
  inputValue,
  onChange,
  onInputChange,
  onFocus,
  onKeyDown,
  options,
  placeholder,
  wrapperClassName,
  isMenuOpen,
  value,
  isInvalid,
}) => {
  return (
    <MultiSelectDropdownInputBase
      wrapperClassName={wrapperClassName}
      styles={{
        control: base => controlStyles(base, isInvalid),
        multiValue: multiValueStyles,
        multiValueLabel: multiValueLabelStyles,
        multiValueRemove: multiValueRemoveStyles,
        option: optionStyles,
        placeholder: placeholderStyles,
        valueContainer: valueContainerStyles,
      }}
      isClearable={false}
      className={className}
      onFocus={onFocus}
      isMenuOpen={isMenuOpen}
      onChange={onChange}
      onInputChange={onInputChange}
      onKeyDown={onKeyDown}
      options={options}
      value={value}
      placeholder={placeholder}
      inputValue={inputValue}
    />
  );
};
